<div class="w-full flex flex-col pt-5 gap-md">
  <div class="flex items-center gap-lg">
    <span class="text-4xl font-bold">
      {{ "MasterData.Users.Title" | i18nextEager }}
    </span>
    @if ((divisions.value()?.data ?? []).length > 1) {
      <doka-select class="w-48" [value]="effectiveDivisionId()" (valueChange)="onDivisionChange($any($event))">
        @for (division of divisions.value()?.data ?? []; track division.id) {
          <doka-select-option [value]="division.id" [label]="division.name"></doka-select-option>
        }
      </doka-select>
    }
  </div>
  <doka-search-field (valueChange)="searchText.set($event)" [placeholder]="'Common.Search' | i18nextEager" />

  @if (hierarchy.isLoading()) {
    <app-spinner />
  } @else if (hierarchy.error()) {
    <div class="flex items-center gap-sm text-text-error py-4">
      <doka-icon name="error" />
      <span>{{ "MasterData.Users.LoadError" | i18nextEager }}</span>
    </div>
  } @else {
    <ul class="flex flex-col gap-sm">
      <!-- Division-level users -->
      @if (divisionUsers().length > 0 || effectiveDivisionId()) {
        <li>
          <div class="flex items-center gap-base rounded-md px-2 py-1 bg-background-gray-ultra-light">
            <doka-icon name="domain" class="text-text-light" />
            <span class="font-bold">{{ divisionName() }}</span>
            @if (divisionUsers().length > 0) {
              <span class="text-sm text-text-light">
                ({{ divisionUsers().length }} {{ "MasterData.Users.DivisionUsers" | i18nextEager }})
              </span>
            }
            <a
              class="action-icon ml-auto"
              [title]="'MasterData.Users.AddUser' | i18nextEager"
              (click)="openAssignModal('division', effectiveDivisionId()!, divisionName(), getUserIds(divisionUsers()))"
              role="button"
            >
              <doka-icon name="person_add" />
            </a>
          </div>
          @for (user of divisionUsers(); track user.id) {
            <div class="flex items-center gap-base pl-12 py-1 group">
              <doka-icon name="person" class="text-text-light" />
              <span class="text-sm">{{ formatUserName(user) }}</span>
              <a
                class="action-icon opacity-0 group-hover:opacity-100 transition-opacity"
                [title]="'MasterData.Users.RemoveUser' | i18nextEager"
                (click)="removeUser('division', effectiveDivisionId()!, user)"
                role="button"
              >
                <doka-icon name="close" />
              </a>
            </div>
          }
        </li>
      }

      @for (region of filteredRegions(); track region.id) {
        <li>
          <!-- Region -->
          <div
            class="flex items-center gap-base cursor-pointer rounded-md px-2 py-1 hover:bg-background-gray-ultra-light"
            (click)="toggleRegion(region.id)"
          >
            <doka-icon [name]="isRegionExpanded(region.id) ? 'keyboard_arrow_down' : 'keyboard_arrow_right'" />
            <doka-icon name="globe" class="text-text-light" />
            <span class="font-bold">{{ region.name }}</span>
            @if (region.users.length > 0) {
              <span class="text-sm text-text-light">
                ({{ region.users.length }} {{ "MasterData.Users.RegionalDirectors" | i18nextEager }})
              </span>
            }
            <a
              class="action-icon ml-auto"
              [title]="'MasterData.Users.AddUser' | i18nextEager"
              (click)="
                $event.stopPropagation(); openAssignModal('region', region.id, region.name, getUserIds(region.users))
              "
              role="button"
            >
              <doka-icon name="person_add" />
            </a>
          </div>

          @if (isRegionExpanded(region.id)) {
            <!-- Region Users -->
            @for (user of region.users; track user.id) {
              <div class="flex items-center gap-base pl-12 py-1 group">
                <doka-icon name="person" class="text-text-light" />
                <span class="text-sm">{{ formatUserName(user) }}</span>
                <a
                  class="action-icon opacity-0 group-hover:opacity-100 transition-opacity"
                  [title]="'MasterData.Users.RemoveUser' | i18nextEager"
                  (click)="removeUser('region', region.id, user)"
                  role="button"
                >
                  <doka-icon name="close" />
                </a>
              </div>
            }

            <!-- Countries -->
            <ul class="pl-5">
              @for (country of region.countries; track country.id) {
                <li>
                  <div
                    class="flex items-center gap-base cursor-pointer rounded-md px-2 py-1 hover:bg-background-gray-ultra-light"
                    (click)="toggleCountry(country.id)"
                  >
                    <doka-icon
                      [name]="isCountryExpanded(country.id) ? 'keyboard_arrow_down' : 'keyboard_arrow_right'"
                    />
                    <doka-icon name="map" class="text-text-light" />
                    <span class="font-bold">{{ country.name }}</span>
                    @if (country.users.length > 0) {
                      <span class="text-sm text-text-light">
                        ({{ country.users.length }} {{ "MasterData.Users.HeadsOfCountry" | i18nextEager }})
                      </span>
                    }
                    <a
                      class="action-icon ml-auto"
                      [title]="'MasterData.Users.AddUser' | i18nextEager"
                      (click)="
                        $event.stopPropagation();
                        openAssignModal('country', country.id, country.name, getUserIds(country.users))
                      "
                      role="button"
                    >
                      <doka-icon name="person_add" />
                    </a>
                  </div>

                  @if (isCountryExpanded(country.id)) {
                    <!-- Country Users -->
                    @for (user of country.users; track user.id) {
                      <div class="flex items-center gap-base pl-12 py-1 group">
                        <doka-icon name="person" class="text-text-light" />
                        <span class="text-sm">{{ formatUserName(user) }}</span>
                        <a
                          class="action-icon opacity-0 group-hover:opacity-100 transition-opacity"
                          [title]="'MasterData.Users.RemoveUser' | i18nextEager"
                          (click)="removeUser('country', country.id, user)"
                          role="button"
                        >
                          <doka-icon name="close" />
                        </a>
                      </div>
                    }

                    <!-- Branches -->
                    <ul class="pl-5">
                      @for (branch of country.branches; track branch.id) {
                        <li>
                          <div
                            class="flex items-center gap-base cursor-pointer rounded-md px-2 py-1 hover:bg-background-gray-ultra-light"
                            (click)="toggleBranch(branch.id)"
                          >
                            <doka-icon
                              [name]="isBranchExpanded(branch.id) ? 'keyboard_arrow_down' : 'keyboard_arrow_right'"
                            />
                            <doka-icon name="location_on" class="text-text-light" />
                            <span class="font-bold">{{ branch.name }}</span>
                            @if (branch.users.length > 0) {
                              <span class="text-sm text-text-light">
                                ({{ branch.users.length }} {{ "MasterData.Users.BranchWorkers" | i18nextEager }})
                              </span>
                            }
                            <a
                              class="action-icon ml-auto"
                              [title]="'MasterData.Users.AddUser' | i18nextEager"
                              (click)="
                                $event.stopPropagation();
                                openAssignModal('branch', branch.id, branch.name, getUserIds(branch.users))
                              "
                              role="button"
                            >
                              <doka-icon name="person_add" />
                            </a>
                          </div>

                          @if (isBranchExpanded(branch.id)) {
                            @for (user of branch.users; track user.id) {
                              <div class="flex items-center gap-base pl-12 py-1 group">
                                <doka-icon name="person" class="text-text-light" />
                                <span class="text-sm">{{ formatUserName(user) }}</span>
                                <a
                                  class="action-icon opacity-0 group-hover:opacity-100 transition-opacity"
                                  [title]="'MasterData.Users.RemoveUser' | i18nextEager"
                                  (click)="removeUser('branch', branch.id, user)"
                                  role="button"
                                >
                                  <doka-icon name="close" />
                                </a>
                              </div>
                            }
                            @if (branch.users.length === 0) {
                              <div class="pl-12 py-1 text-sm text-text-light italic">
                                {{ "MasterData.Users.NoUsers" | i18nextEager }}
                              </div>
                            }
                          }
                        </li>
                      }
                    </ul>
                  }
                </li>
              }
            </ul>
          }
        </li>
      }
    </ul>
  }
</div>
